// Historique cycle de vie — append-only, jamais éditable.
import { useEffect, useState } from "react";
import { Clock, X } from "lucide-react";
import { api } from "@/lib/api";
import {
  NAVY, CHAMPAGNE, SLATE, IVORY, GOLD, Loader,
} from "@/components/altiora/PremiumUI";
import {
  humanStatus, statusPillStyle, humanReason, humanSource, humanSignal,
} from "@/lib/altiora-leads-i18n";

export default function LeadHistoryDrawer({ entityId, entityName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(
          `/leads-engine/leads/${encodeURIComponent(entityId)}/history`);
        if (!cancel) setEvents(r.data?.events || []);
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [entityId]);

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="history-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose} />
      <aside className="w-full sm:w-[440px] h-full overflow-y-auto shadow-2xl"
             style={{ background: IVORY }}>
        <header className="sticky top-0 px-6 py-4 flex items-center gap-3"
                style={{ background: NAVY, color: IVORY }}>
          <Clock className="h-4 w-4" style={{ color: GOLD }} />
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-[0.24em]"
                 style={{ color: GOLD }}>Historique</div>
            <h3 className="font-serif text-[16px]">{entityName}</h3>
          </div>
          <button onClick={onClose} data-testid="history-close"
                  className="p-1 rounded hover:bg-white/10">
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="p-6 space-y-3">
          {loading && <Loader label="Chargement…" />}
          {!loading && events.length === 0 && (
            <p className="text-[12px]" style={{ color: SLATE }}>
              Aucun événement enregistré.
            </p>
          )}
          {!loading && events.map((e, i) => {
            const fromStyle = e.from_status
                                    ? statusPillStyle(e.from_status) : null;
            const toStyle = statusPillStyle(e.to_status);
            return (
              <div key={e.history_id || i}
                   data-testid={`history-event-${i}`}
                   className="rounded-lg p-3 bg-white"
                   style={{ border: `1px solid ${CHAMPAGNE}44` }}>
                <div className="flex items-center gap-2 flex-wrap">
                  {fromStyle && (
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded-full"
                          style={{ background: fromStyle.bg,
                                    color: fromStyle.color }}>
                      {humanStatus(e.from_status)}
                    </span>
                  )}
                  <span style={{ color: SLATE }}>→</span>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full"
                        style={{ background: toStyle.bg,
                                  color: toStyle.color }}>
                    {humanStatus(e.to_status)}
                  </span>
                  <span className="text-[10px] font-mono ml-auto"
                        style={{ color: SLATE }}>
                    {e.at?.slice(0, 16).replace("T", " ")}
                  </span>
                </div>
                {e.comment && (
                  <p className="mt-2 text-[12px]" style={{ color: NAVY }}>
                    {e.comment}
                  </p>
                )}
                {e.close_reason && (
                  <p className="mt-1 text-[11px]" style={{ color: "#B76E00" }}>
                    Motif : {humanReason(e.close_reason)}
                  </p>
                )}
                <div className="mt-2 text-[10px] flex items-center gap-2 flex-wrap"
                     style={{ color: SLATE }}>
                  {(e.provenance || []).length > 0 && (
                    <span>Provenance : {(e.provenance || [])
                                                .map(humanSource).join(" · ")}</span>
                  )}
                  {e.trigger_signal && (
                    <span>Signal déclencheur : {humanSignal(e.trigger_signal)}</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </aside>
    </div>
  );
}
